import { useMemo } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import {
  featuresForAdminPath,
  firstAdminPath,
  hasAnyPermission,
  hasPermission,
  type AdminFeatureId,
} from "@tih/shared";
import { RequireAdmin } from "../../components/RequireAuth";
import { AdminIcon, type AdminIconName } from "../../components/admin/AdminIcon";
import { useAuth } from "../../context/AuthContext";
import { GalleryUploadProvider } from "../../context/GalleryUploadContext";
import { GalleryUploadToast } from "../../components/admin/GalleryUploadToast";

type NavItem = {
  to: string;
  label: string;
  end?: boolean;
  icon: AdminIconName;
  /** Any of these features unlocks the link. */
  features: AdminFeatureId[];
};

const TOP_LINKS: NavItem[] = [
  { to: "/admin", label: "Dashboard", end: true, icon: "dashboard", features: ["dashboard"] },
  {
    to: "/admin/tonight",
    label: "Tonight",
    icon: "tonight",
    features: ["check_in", "attendance", "events", "rsvps"],
  },
  {
    to: "/admin/content-hub",
    label: "Content",
    icon: "content",
    features: ["content", "events"],
  },
  {
    to: "/admin/people",
    label: "People",
    icon: "people",
    features: ["inbox", "sms"],
  },
];

function navLinkClass(isActive: boolean): string {
  return ["admin-nav-link", "admin-nav-link--icon", isActive ? "is-active" : ""]
    .filter(Boolean)
    .join(" ");
}

export function AdminLayout() {
  return (
    <RequireAdmin>
      <GalleryUploadProvider>
        <AdminShell />
        <GalleryUploadToast />
      </GalleryUploadProvider>
    </RequireAdmin>
  );
}

function AdminShell() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();

  const visibleTop = useMemo(
    () => TOP_LINKS.filter((item) => hasAnyPermission(user, item.features)),
    [user],
  );
  const showSettings = hasAnyPermission(user, ["settings", "sms", "users"]);
  const needed = featuresForAdminPath(pathname);
  const allowedHere = !needed || hasAnyPermission(user, needed);

  if (!allowedHere) {
    return <Navigate to={firstAdminPath(user)} replace />;
  }

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <Link to={firstAdminPath(user)} className="admin-brand" aria-label="Together In Heaven Admin">
          <img src="/assets/logo.png" alt="Together In Heaven" className="admin-brand-logo" />
        </Link>
        <nav className="admin-nav" aria-label="Admin sections">
          {visibleTop.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) => navLinkClass(isActive)}
            >
              <AdminIcon name={item.icon} size={18} className="admin-nav-icon" />
              <span>{item.label}</span>
            </NavLink>
          ))}

          {showSettings && (
            <NavLink
              to="/admin/settings"
              className={({ isActive }) => navLinkClass(isActive)}
            >
              <AdminIcon name="settings" size={18} className="admin-nav-icon" />
              <span>Settings</span>
            </NavLink>
          )}
        </nav>
        <div className="admin-sidebar-footer">
          <div className="admin-user-name">{user?.name}</div>
          <div className="admin-user-email">{user?.email}</div>
          <button type="button" className="admin-signout" onClick={() => void logout()}>
            Sign out
          </button>
        </div>
      </aside>
      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
}
